import React, { useState, useEffect } from "react";

const AdminContactNumbers = () => {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

    const [contacts, setContacts] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState(""); // ← New: for filtering

    const [form, setForm] = useState({ key: "", numbers: [""] });
    const [editingKey, setEditingKey] = useState(null);

    // Helper: Format number with +91 prefix if it's 10 digits
    const formatNumber = (num) => {
        const cleaned = num.replace(/\D/g, "");
        if (cleaned.length === 10) {
            return `+91 ${cleaned}`;
        }
        return num;
    };

    const fetchContacts = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${apiBaseUrl}/api/contact-numbers`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            if (data.success && data.data) {
                setContacts(data.data);
            }
        } catch (err) {
            setError(`Failed to load: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContacts();
    }, []);

    const handleSave = async () => {
        if (!form.key.trim() || form.numbers.some((n) => !n.trim())) {
            return alert("Location key and at least one number required");
        }

        const payload = {
            key: form.key.trim(),
            numbers: form.numbers
                .map((n) => n.trim())
                .map((n) => {
                    const cleaned = n.replace(/\D/g, "");
                    return cleaned.length === 10 ? `+91${cleaned}` : n;
                }),
        };

        try {
            const method = editingKey ? "PUT" : "POST";
            const url = editingKey
                ? `${apiBaseUrl}/api/contact-numbers/${editingKey}`
                : `${apiBaseUrl}/api/contact-numbers`;

            const res = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) throw new Error("Save failed");
            alert(editingKey ? "Updated!" : "Added!");
            setForm({ key: "", numbers: [""] });
            setEditingKey(null);
            fetchContacts();
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    const handleEdit = (key) => {
        const item = contacts[key];
        setForm({
            key,
            numbers: [...(item.numbers || [""])],
        });
        setEditingKey(key);
    };

    const handleDelete = async (key) => {
        if (key === "default") return alert("Can't delete default");
        if (!window.confirm(`Delete "${key}"?`)) return;

        try {
            const res = await fetch(`${apiBaseUrl}/api/contact-numbers/${key}`, { method: "DELETE" });
            if (!res.ok) throw new Error("Delete failed");
            alert("Deleted!");
            fetchContacts();
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    const addNumber = () => setForm((p) => ({ ...p, numbers: [...p.numbers, ""] }));

    const removeNumber = (idx) => {
        if (form.numbers.length <= 1) return;
        setForm((p) => ({ ...p, numbers: p.numbers.filter((_, i) => i !== idx) }));
    };

    // Filter contacts based on search term (case-insensitive)
    const filteredContacts = Object.fromEntries(
        Object.entries(contacts).filter(([key]) => key.toLowerCase().includes(searchTerm.toLowerCase().trim()))
    );

    if (loading) return <div className="p-10 text-center">Loading...</div>;

    return (
        <div className="max-w-6xl mx-auto pt-10">
            <h1 className="text-3xl font-bold mb-6 text-center">Manage Contact Numbers</h1>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 p-4 mb-8 rounded text-center">{error}</div>
            )}

            {/* FORM - TOP */}
            <div className="bg-white px-6 py-3 mb-2 border rounded-xl shadow-md">
                <h2 className="text-2xl font-bold mt-2">{editingKey ? `Edit "${editingKey}"` : "Add New Location"}</h2>

                <div className="grid grid-cols-1 gap-4">
                    {/* Location Key */}
                    <div>
                        <label className="block text-sm font-medium mb-1 text-left">Location</label>
                        <input
                            type="text"
                            value={form.key}
                            onChange={(e) => setForm({ ...form, key: e.target.value.trim() })}
                            disabled={!!editingKey}
                            className="w-full px-4 py-3 border rounded-lg disabled:bg-gray-100"
                            placeholder="e.g. Thrissur"
                        />
                    </div>

                    {/* Phone Numbers */}
                    <div>
                        <div className="flex justify-between items-center mb-1">
                            <label className="block text-sm font-medium">Phone Numbers</label>
                            <button
                                type="button"
                                onClick={addNumber}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                                + Add Number
                            </button>
                        </div>

                        {form.numbers.map((num, idx) => (
                            <div key={idx} className="flex gap-2 mb-1">
                                <input
                                    type="text"
                                    value={num}
                                    onChange={(e) => {
                                        const newNums = [...form.numbers];
                                        newNums[idx] = formatNumber(e.target.value);
                                        setForm({ ...form, numbers: newNums });
                                    }}
                                    className="flex-1 px-4 py-3 border rounded-lg"
                                    placeholder="+91 9999999999"
                                />
                                {form.numbers.length > 1 && (
                                    <button
                                        onClick={() => removeNumber(idx)}
                                        className="px-4 py-3 bg-red-50 text-red-600 rounded hover:bg-red-100"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex gap-4 mt-3">
                    <button
                        onClick={handleSave}
                        className="flex-1 px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold"
                    >
                        {editingKey ? "Update" : "Add"}
                    </button>
                    {editingKey && (
                        <button
                            onClick={() => {
                                setForm({ key: "", numbers: [""] });
                                setEditingKey(null);
                            }}
                            className="flex-1 px-8 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-bold"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </div>

            {/* TABLE SECTION - With Dynamic Filter */}
            <div className="mt-8">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold">All Locations</h2>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Filter by location..."
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                    />
                </div>

                <div className="overflow-x-auto border rounded-xl shadow-md">
                    <table className="min-w-full bg-white">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-4 text-center font-bold text-gray-700">Location</th>
                                <th className="px-6 py-4 text-center font-bold text-gray-700">Phone Numbers</th>
                                <th className="px-6 py-4 text-center font-bold text-gray-700">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {Object.keys(filteredContacts).length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="px-6 py-12 text-center text-gray-500">
                                        {searchTerm ? "No matching locations found" : "No contact entries yet"}
                                    </td>
                                </tr>
                            ) : (
                                Object.entries(filteredContacts).map(([key, value]) => (
                                    <tr key={key} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 font-semibold text-center">{key}</td>
                                        <td className="px-6 py-4 text-red-600 font-medium text-center">
                                            {value.numbers?.map((n) => `📞 ${n}`).join("  ") || "—"}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {key !== "default" ? (
                                                <div className="flex justify-center gap-4">
                                                    <button
                                                        onClick={() => handleEdit(key)}
                                                        className="text-blue-600 hover:text-blue-800"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(key)}
                                                        className="text-red-600 hover:text-red-800"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-gray-400 italic">Default (protected)</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminContactNumbers;
